/*
 * <cart-discount> — collapsible "Do you have a discount code?" field for the cart drawer.
 *
 * Shopify has no AJAX endpoint for applying discount codes, so we use the supported
 * discount link (`/discount/{code}`), which sets the discount on the cart, then we
 * re-render the cart drawer body via the Section Rendering API so the totals update
 * live without a full page reload or closing the drawer.
 */

// Survives the innerHTML swap below: when the cart drawer body is re-rendered, the
// previous <cart-discount> is destroyed and a fresh one is upgraded by the browser.
// Its connectedCallback reads this to restore the open panel + any error feedback.
let pendingFeedback = null;

const discountErrorMessage = () =>
  (window.cartStrings && window.cartStrings.discountError) || 'Enter a valid discount code.';

const shopRoot = () => {
  const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  return root || '/';
};

class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-discount-toggle]');
    this.input = this.querySelector('[data-discount-input]');
    this.applyBtn = this.querySelector('[data-discount-apply]');
    this.errorEl = this.querySelector('[data-discount-error]');

    this.toggleBtn?.addEventListener('click', () => this.togglePanel());
    this.applyBtn?.addEventListener('click', event => {
      event.preventDefault();
      this.apply();
    });
    this.input?.addEventListener('keydown', event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.apply();
      }
    });
    this.input?.addEventListener('input', () => this.clearError());

    // Restore UI state after a section re-render replaced the previous instance.
    if (pendingFeedback) {
      this.openPanel();
      if (pendingFeedback.error) {
        if (this.input) this.input.value = pendingFeedback.value || '';
        this.showError(pendingFeedback.error);
        this.input?.focus();
      }
      pendingFeedback = null;
    }
  }

  togglePanel() {
    if (this.classList.contains('is-open')) {
      this.closePanel();
    } else {
      this.openPanel();
      this.input?.focus();
    }
  }

  openPanel() {
    this.classList.add('is-open');
    this.toggleBtn?.setAttribute('aria-expanded', 'true');
  }

  closePanel() {
    this.classList.remove('is-open');
    this.toggleBtn?.setAttribute('aria-expanded', 'false');
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    if (this.applyBtn) this.applyBtn.disabled = isLoading;
    if (this.input) this.input.disabled = isLoading;
  }

  async apply() {
    const code = (this.input?.value || '').trim();
    if (!code) {
      this.showError(discountErrorMessage());
      return;
    }

    this.clearError();
    this.setLoading(true);

    try {
      // Applying the discount link sets it on the current cart (follows the redirect).
      await fetch(`${shopRoot()}discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        cache: 'no-store'
      });

      // Pull the freshly rendered cart drawer body to reflect the new totals.
      const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`, {
        cache: 'no-store'
      });
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const newBody = doc.querySelector('#CartDrawer-Body');
      const currentBody = document.querySelector('#CartDrawer-Body');

      if (!newBody || !currentBody) {
        this.setLoading(false);
        this.showError(discountErrorMessage());
        return;
      }

      const appliedCodes = (doc.querySelector('cart-discount')?.dataset.applied || '').toLowerCase();
      const success = appliedCodes
        .split('|')
        .filter(Boolean)
        .includes(code.toLowerCase());

      // Hand feedback to the new instance, then swap. The browser upgrades the
      // incoming <cart-discount>, whose connectedCallback consumes pendingFeedback.
      pendingFeedback = success ? { error: null } : { error: discountErrorMessage(), value: code };
      currentBody.innerHTML = newBody.innerHTML;
    } catch (error) {
      console.error('Discount apply failed:', error);
      this.setLoading(false);
      this.showError(discountErrorMessage());
    }
  }
}

customElements.define('cart-discount', CartDiscount);
